import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import messages from './local/index';

// Helper to deeply merge objects — SOURCE values overwrite TARGET values
function deepMerge(target: Record<string, unknown>, source: Record<string, unknown>): Record<string, unknown> {
  const result: Record<string, unknown> = { ...target };
  Object.keys(source).forEach((key) => {
    const sourceVal = source[key];
    const targetVal = result[key];
    if (
      typeof sourceVal === 'object' &&
      sourceVal !== null &&
      !Array.isArray(sourceVal) &&
      typeof targetVal === 'object' &&
      targetVal !== null &&
      !Array.isArray(targetVal)
    ) {
      result[key] = deepMerge(
        targetVal as Record<string, unknown>,
        sourceVal as Record<string, unknown>
      );
    } else {
      result[key] = sourceVal;
    }
  });
  return result;
}

const enHome = deepMerge(
  {
    header: {
      nav_home: 'Home',
      nav_about: 'About Us',
      nav_services: 'Services',
      nav_contact: 'Contact',
    },
    brand: {
      tagline_linking: 'Linking People & Business',
      tagline_excellence: 'Driving to excellence with excellent drivers …',
    },
    hero: {
      slide1_title: 'New markets, new business ... no risk!',
      slide2_title: 'With us straight into the heart of Europe!',
      slide3_title: 'Germany (D-A-CH) ...\n... successfully\nwith a\nlocal partner!',
      slide4_title: 'Together with us!',
      slide4_subtitle: 'Business Development\nConsulting Services\nInterim Management\nTraining & Coaching',
      slide5_title: 'Press\nSTART ...\n... and\nlet\'s go!',
      prev_slide: 'Previous slide',
      next_slide: 'Next slide',
      slide_label: 'Slide {{number}}',
    },
  } as Record<string, unknown>,
  (messages.en?.home || {}) as Record<string, unknown>
);

const deHome = deepMerge(
  {
    header: {
      nav_home: 'Home',
      nav_about: 'Über uns',
      nav_services: 'Services',
      nav_contact: 'Kontakt',
    },
    brand: {
      tagline_linking: 'Linking People & Business',
      tagline_excellence: 'Driving to excellence with excellent drivers …',
    },
    hero: {
      slide1_title: 'Neue Märkte, neues Geschäft ... kein Risiko!',
      slide2_title: 'Mit uns mitten ins Herz Europas!',
      slide3_title: 'Deutschland (D-A-CH) ...\n... erfolgreich\nmit einem\nlokalen Partner!',
      slide4_title: 'Zusammen mit uns!',
      slide4_subtitle: 'Business Development\nConsulting\nInterim Management\nTraining & Coaching',
      slide5_title: 'Drücken Sie\nauf START ...\n... und\nlos geht\'s!',
      prev_slide: 'Vorherige Folie',
      next_slide: 'Nächste Folie',
      slide_label: 'Folie {{number}}',
    },
  } as Record<string, unknown>,
  (messages.de?.home || {}) as Record<string, unknown>
);

const frHome = deepMerge(
  {
    header: {
      nav_home: 'Accueil',
      nav_about: 'À propos',
      nav_services: 'Services',
      nav_contact: 'Contact',
    },
    brand: {
      tagline_linking: 'Linking People & Business',
      tagline_excellence: 'Driving to excellence with excellent drivers …',
    },
    hero: {
      slide1_title: 'Nouveaux marchés, nouvelles affaires ... aucun risque !',
      slide2_title: 'Avec nous, en plein cœur de l\'Europe !',
      slide3_title: 'Allemagne (D-A-CH) ...\n... avec succès\navec un\npartenaire local!',
      slide4_title: 'Ensemble avec nous!',
      slide4_subtitle: 'Développement commercial\nConseil\nManagement de transition\nFormation et coaching',
      slide5_title: 'Appuyez sur\nSTART ...\n... et\nc\'est parti!',
      prev_slide: 'Diapositive précédente',
      next_slide: 'Diapositive suivante',
      slide_label: 'Diapositive {{number}}',
    },
  } as Record<string, unknown>,
  (messages.fr?.home || {}) as Record<string, unknown>
);

const esHome = deepMerge(
  {
    header: {
      nav_home: 'Inicio',
      nav_about: 'Sobre nosotros',
      nav_services: 'Servicios',
      nav_contact: 'Contacto',
    },
    brand: {
      tagline_linking: 'Linking People & Business',
      tagline_excellence: 'Driving to excellence with excellent drivers …',
    },
    hero: {
      slide1_title: 'Nuevos mercados, nuevos negocios ... ¡sin riesgo!',
      slide2_title: '¡Con nosotros, en el corazón de Europa!',
      slide3_title: 'Alemania (D-A-CH) ...\n... con éxito\ncon un\nsocio local!',
      slide4_title: '¡Juntos con nosotros!',
      slide4_subtitle: 'Desarrollo de negocio\nConsultoría\nInterim management\nFormación y coaching',
      slide5_title: '¡Presione\nSTART ...\n... y\nadelante!',
      prev_slide: 'Diapositiva anterior',
      next_slide: 'Diapositiva siguiente',
      slide_label: 'Diapositiva {{number}}',
    },
  } as Record<string, unknown>,
  (messages.es?.home || {}) as Record<string, unknown>
);

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    fallbackLng: 'en',
    ns: ['home', 'services'],
    defaultNS: 'home',
    debug: false,
    resources: {
      en: {
        home: enHome as Record<string, Record<string, string>>,
        services: (messages.en?.services || {}) as Record<string, Record<string, string>>,
      },
      de: {
        home: deHome as Record<string, Record<string, string>>,
        services: (messages.de?.services || {}) as Record<string, Record<string, string>>,
      },
      fr: {
        home: frHome as Record<string, Record<string, string>>,
        services: (messages.fr?.services || {}) as Record<string, Record<string, string>>,
      },
      es: {
        home: esHome as Record<string, Record<string, string>>,
        services: (messages.es?.services || {}) as Record<string, Record<string, string>>,
      },
    },
    initImmediate: true,
    interpolation: {
      escapeValue: false,
    },
    react: {
      useSuspense: false,
      bindI18n: 'languageChanged loaded',
      bindI18nStore: 'added removed',
    },
  });

export default i18n;